// frontend/src/components/product/FeaturedProducts.js

import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { ShoppingBag, ArrowLeft, ArrowRight, ChevronRight } from 'lucide-react';
import { getFeaturedProducts } from '../../actions/productActions';
import Loader from '../layouts/Loader';

const FeaturedProducts = () => {
    const dispatch = useDispatch();
    const { loading, featuredProducts, error } = useSelector(state => state.featuredProductsState);
    const featuredProductsRef = useRef(null);
    const [scrollPosition, setScrollPosition] = useState(0);
    const [maxScroll, setMaxScroll] = useState(0);
   
    // Fetch featured products on component mount
    useEffect(() => {
        // Force refresh to get latest data
        dispatch(getFeaturedProducts(true));
    }, [dispatch]);
   
    // Update max scroll value when products change and on window resize
    useEffect(() => {
        const updateMaxScroll = () => {
            if (featuredProductsRef.current) {
                const container = featuredProductsRef.current;
                setMaxScroll(container.scrollWidth - container.clientWidth);
            }
        };
       
        // Initial calculation after products load
        if (featuredProducts && featuredProducts.length > 0) {
            // Use setTimeout to ensure DOM has updated
            setTimeout(updateMaxScroll, 100);
        }
       
        // Update on resize as well
        window.addEventListener('resize', updateMaxScroll);
        return () => {
            window.removeEventListener('resize', updateMaxScroll);
        };
    }, [featuredProducts]);

    // Track scroll position
    const handleScroll = () => {
        if (featuredProductsRef.current) {
            setScrollPosition(featuredProductsRef.current.scrollLeft);
           
            // Update maxScroll when scrolling to ensure it's accurate
            setMaxScroll(
                featuredProductsRef.current.scrollWidth -
                featuredProductsRef.current.clientWidth
            );
        }
    };

    // Add scroll event listener
    useEffect(() => {
        const container = featuredProductsRef.current;
        if (container) {
            container.addEventListener('scroll', handleScroll);
            return () => {
                container.removeEventListener('scroll', handleScroll);
            };
        }
    }, []);

    // Improved scroll functions
    const scrollLeft = () => {
        if (featuredProductsRef.current) {
            const container = featuredProductsRef.current;
            const cardWidth = 220; // Width of a product card + margin
            const newPosition = Math.max(0, container.scrollLeft - cardWidth * 2);
           
            container.scrollTo({
                left: newPosition,
                behavior: 'smooth'
            });
        }
    };

    const scrollRight = () => {
        if (featuredProductsRef.current) {
            const container = featuredProductsRef.current;
            const cardWidth = 220; // Width of a product card + margin
           
            // Calculate new position advancing by 2 cards
            const newPosition = container.scrollLeft + cardWidth * 2;
           
            container.scrollTo({
                left: newPosition,
                behavior: 'smooth'
            });
           
            // Force update maxScroll after scrolling
            setTimeout(() => {
                setMaxScroll(container.scrollWidth - container.clientWidth);
            }, 500);
        }
    };

    // Product Card Component
    const ProductCard = ({ product }) => {
        if (!product) return null;
       
        // Helper function to calculate discounted price
        const calculateDiscountedPrice = (originalPrice) => {
            try {
                if (!originalPrice) return 0;
                if (!product.discount || !product.discount.active) return originalPrice;
               
                if (product.discount.type === 'percentage') {
                    return originalPrice - (originalPrice * (product.discount.value / 100));
                }
                return originalPrice - product.discount.value;
            } catch (error) {
                console.error('Error calculating discount:', error);
                return originalPrice || 0;
            }
        };
       
        // Display price based on product price type
        const displayPrice = () => {
            try {
                if (product.priceType === 'perKg') {
                    const originalPrice = product.price || 0;
                    const finalPrice = calculateDiscountedPrice(originalPrice);
                   
                    return (
                        <div className="flex flex-col items-center">
                            {product.discount?.active && (
                                <span className="text-gray-500 line-through text-xs mr-2">
                                    ₹{originalPrice.toFixed(2)}
                                </span>
                            )}
                            <div className="text-[#dc2626] font-bold text-sm">
                                ₹{finalPrice.toFixed(2)}
                                <span className="text-xs font-normal ml-1">/kg</span>
                            </div>
                        </div>
                    );
                } else if (product.priceType === 'perDozen') {
                    const originalPrice = product.dozenPrice || 0;
                    const finalPrice = calculateDiscountedPrice(originalPrice);
                   
                    return (
                        <div className="flex flex-col items-center">
                            {product.discount?.active && (
                                <span className="text-gray-500 line-through text-xs mr-2">
                                    ₹{originalPrice.toFixed(2)}
                                </span>
                            )}
                            <div className="text-[#dc2626] font-bold text-sm">
                                ₹{finalPrice.toFixed(2)}
                                <span className="text-xs font-normal ml-1">/dozen</span>
                            </div>
                        </div>
                    );
                } else if (product.priceType === 'perPack') {
                    const originalPrice = product.packPrice || 0;
                    const finalPrice = calculateDiscountedPrice(originalPrice);
                   
                    return (
                        <div className="flex flex-col items-center">
                            {product.discount?.active && (
                                <span className="text-gray-500 line-through text-xs mr-2">
                                    ₹{originalPrice.toFixed(2)}
                                </span>
                            )}
                            <div className="text-[#dc2626] font-bold text-sm">
                                ₹{finalPrice.toFixed(2)}
                                <span className="text-xs font-normal ml-1">/pack</span>
                            </div>
                        </div>
                    );
                } else if (product.weightOptions && Array.isArray(product.weightOptions) && product.weightOptions.length > 0) {
                    // For fixed weight options (500g, 1kg) - Only show visible ones
                    const visibleOptions = product.weightOptions.filter(opt => opt.visible);
                   
                    if (visibleOptions.length === 0) {
                        return (
                            <div className="text-gray-500 text-xs">
                                Currently unavailable
                            </div>
                        );
                    }
                   
                    if (visibleOptions.length === 1) {
                        const option = visibleOptions[0];
                        const originalPrice = option.price || 0;
                        const finalPrice = calculateDiscountedPrice(originalPrice);
                       
                        return (
                            <div className="flex flex-col items-center">
                                {product.discount?.active && (
                                    <span className="text-gray-500 line-through text-xs">
                                        ₹{originalPrice.toFixed(2)}
                                    </span>
                                )}
                                <div className="text-[#dc2626] font-bold text-sm">
                                    ₹{finalPrice.toFixed(2)}
                                    <span className="text-xs font-normal ml-1">{option.weight}</span>
                                </div>
                            </div>
                        );
                    }
                   
                    // If we have multiple visible options, show price range
                    const prices = visibleOptions.map(opt => {
                        const originalPrice = opt.price || 0;
                        return calculateDiscountedPrice(originalPrice);
                    });
                   
                    const minPrice = Math.min(...prices);
                    const maxPrice = Math.max(...prices);
                   
                    return (
                        <div className="text-[#dc2626] font-bold text-sm">
                            ₹{minPrice.toFixed(2)} - ₹{maxPrice.toFixed(2)}
                        </div>
                    );
                }
               
                return (
                    <div className="text-[#dc2626] font-bold text-sm">
                        ₹{(product.price || 0).toFixed(2)}
                    </div>
                );
            } catch (error) {
                console.error('Error displaying price:', error);
                return <p className="text-gray-500 text-xs">Price unavailable</p>;
            }
        };
       
        // Helper function to handle different image URL formats
        const getImageUrl = (imageSource) => {
            if (!imageSource) return 'https://via.placeholder.com/400x300?text=Image+Not+Available';
           
            if (imageSource.includes('amazonaws.com')) {
                return imageSource;
            }
           
            if (imageSource.startsWith('/')) {
                return `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}${imageSource}`;
            }
           
            return imageSource;
        };

        return (
            <div className="transform transition-transform hover:-translate-y-1">
                <Link to={`/product/${product._id}`} className="block relative">
                    <div className="relative h-40 w-full">
                        <div className="w-full h-full overflow-hidden">
                            {product.images && product.images[0] ? (
                                <img
                                    src={getImageUrl(product.images[0].image)}
                                    alt={product.name || 'Product'}
                                    className="w-full h-full object-contain"
                                    onError={(e) => {
                                        console.error("Image failed to load:", product.images[0].image);
                                        e.target.onerror = null;
                                        e.target.src = 'https://via.placeholder.com/400x300?text=Image+Not+Available';
                                    }}
                                />
                            ) : (
                                <div className="w-full h-full flex items-center justify-center">
                                    <ShoppingBag className="w-10 h-10 text-gray-400" />
                                </div>
                            )}
                        </div>
                        {product.discount?.active && (
                            <div className="absolute top-2 right-2 bg-red-500 text-white px-2 py-0.5 rounded-full text-xs font-medium">
                                {product.discount.type === 'percentage'
                                    ? `${product.discount.value}% OFF`
                                    : `₹${product.discount.value} OFF`
                                }
                            </div>
                        )}
                    </div>
                </Link>
                <div className="pt-2 flex flex-col items-center text-center">
                    <Link to={`/product/${product._id}`} className="block w-full">
                        <h3 className="font-medium text-xs text-gray-800 line-clamp-1 mb-1 hover:text-[#dc2626] transition-colors">
                            {product.name || 'Unnamed Product'}
                        </h3>
                    </Link>
                   
                    <div className="flex flex-col items-center mb-2">
                        {displayPrice()}
                    </div>
                    <Link
                        to={`/product/${product._id}`}
                        className="inline-block w-3/4 text-center bg-[#dc2626] text-white py-1 rounded-md text-xs hover:bg-[#dc2626]/90 transition-colors mx-auto"
                    >
                        View Details
                    </Link>
                </div>
            </div>
        );
    };

    return (
        <div className="container mx-auto px-4 py-8">
           <div className="flex justify-between items-center mb-6">
                <h2 className="text-lg md:text-2xl font-bold text-red-600 font-['Crimson_Text'] whitespace-nowrap">
                    Featured Products
                </h2>
                <Link to="/shop" className="flex items-center gap-2 text-red-600 hover:text-red-700 transition-colors no-underline text-sm md:text-base">
                    <span>View All</span>
                    <ChevronRight className="w-4 h-4" />
                </Link>
            </div>
           
            {loading ? <Loader /> : (
                !featuredProducts || featuredProducts.length === 0 ? (
                    <div className="text-center py-8 bg-white bg-opacity-80 rounded-lg">
                        <p>No products available at the moment. Please check back later.</p>
                    </div>
                ) : (
                    <div className="relative">
                        <button
                            onClick={scrollLeft}
                            className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white p-2 rounded-full shadow-md hover:bg-gray-100 transition-colors"
                            aria-label="Previous products"
                        >
                            <ArrowLeft className="w-5 h-5 text-gray-700" />
                        </button>
                       
                        <div
                            ref={featuredProductsRef}
                            className="flex space-x-6 py-4 px-10 overflow-x-auto scrollbar-hide scroll-smooth"
                            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                        >
                            {featuredProducts.map((product, index) => (
                                <div
                                    key={`${product._id}-${index}`}
                                    className="flex-shrink-0 w-[220px]"
                                >
                                    <ProductCard product={product} />
                                </div>
                            ))}
                        </div>
                       
                        <button
                            onClick={scrollRight}
                            className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white p-2 rounded-full shadow-md hover:bg-gray-100 transition-colors"
                            aria-label="Next products"
                        >
                            <ArrowRight className="w-5 h-5 text-gray-700" />
                        </button>
                    </div>
                )
            )}
        </div>
    );
};

export default FeaturedProducts;